import { useState, useEffect, useMemo, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import { X, Loader2, Crown, Plus } from '@/components/icons';
import { hapticLight } from '@/lib/haptics';
import { Button } from './ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer';
import { Stepper } from './ui/stepper';
import { PrntrCard, PrntrSwitchRow } from './ui/prntr-input';
import {
  DateObjectRow,
  PhoneRowField,
  PickerRowField,
  SelectRowField,
  TextRow,
  TextareaField,
} from './ui/prntr-fields';
import { NationalityPickerDrawer } from './NationalityPickerDrawer';
import { AirportPickerDrawer } from './AirportPickerDrawer';
import { CountryCodePickerDrawer } from './CountryCodePickerDrawer';
import { DocumentFormDrawer } from './documents/DocumentFormDrawer';
import type { StagedDocument } from './documents/types';
import { DocumentsGroupCard } from './DocumentsGroupCard';
import { EmptyStateCard } from './EmptyStateCard';
import { useDocumentCreate } from '../lib/hooks/document';
import { useAirportByIcao } from '../lib/hooks/airport';
import { useCrewGetById, useCrewGetMe, useCrewCreate, useCrewUpdate, useCrewCheckEmailExists, useCrewSetAdmin, useCrewSetPhoto } from '../lib/hooks/crew';
import { generateCrewPhotoS3Key, generateS3Key, uploadFileWithPresignedPost, useS3GetUploadPost } from '../lib/hooks/s3';
import { track, EVENTS } from '@/utils/analytics';
import { ProfilePhotoPicker, ProfilePhotoPickerHandle } from './ProfilePhotoPicker';
import { ProfileAvatar } from './ProfileAvatar';
import { CrewPreferencePhotos } from './CrewPreferencePhotos';
import { StagedPhotoGridHandle } from './StagedPhotoGrid';
import { cn, getInitials } from '../lib/utils';
import { parsePhoneNumber } from '../lib/phone';
import { CrewRole, crewRoles, crewRoleLabels, isPilotRole, DocumentSubType } from '@@api/shared'

const crewRoleOptions = crewRoles.map((role) => ({
  value: role,
  label: crewRoleLabels[role] || role,
}));

// Two-line label for toggle rows (title + caption)
const toggleLabel = (title: string, caption: string) => (
  <span className="flex flex-col py-2">
    <span className="text-content-primary-bold">{title}</span>
    <span className="text-xs font-normal text-content-primary-subtle">{caption}</span>
  </span>
);

// Form validation schema - matches Crew model required fields
const crewFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  phoneCountryCode: z.string().default('+1'),
  phoneNumber: z.string().min(1, 'Phone number is required'),
  baseAirportIcao: z.string().optional(),
  nationality: z.string().optional(),
  dateOfBirth: z.date().optional(),
  emergencyContactName: z.string().optional(),
  emergencyContactRelation: z.string().optional(),
  emergencyCountryCode: z.string().default('+1'),
  emergencyNumber: z.string().optional(),
  medicalInfo: z.string().optional(),
  foodPreferences: z.string().optional(),
  beveragePreferences: z.string().optional(),
  crewRole: z.nativeEnum(CrewRole).nullable().optional(),
  pilotLicenseNumber: z.string().optional(),
  isAdmin: z.boolean().default(false),
  includeInSchedule: z.boolean().default(true),
}).refine(
  (data) => {
    if (!data.phoneNumber) return true;
    // Just validate the phone number part (without country code) - should be 4-15 digits
    const digitsOnly = data.phoneNumber.replace(/[\s\-\(\)]/g, '');
    return /^\d{4,15}$/.test(digitsOnly);
  },
  { message: 'Invalid phone number', path: ['phoneNumber'] }
).refine(
  (data) => {
    if (!data.emergencyNumber) return true;
    // Just validate the phone number part (without country code) - should be 4-15 digits
    const digitsOnly = data.emergencyNumber.replace(/[\s\-\(\)]/g, '');
    return /^\d{4,15}$/.test(digitsOnly);
  },
  { message: 'Invalid phone number', path: ['emergencyNumber'] }
).refine(
  (data) => !isPilotRole(data.crewRole) || !!data.pilotLicenseNumber?.trim(),
  { message: 'Pilot license number is required for pilots', path: ['pilotLicenseNumber'] }
);

type CrewFormData = z.infer<typeof crewFormSchema>;

// Create flow is a 4-step wizard. Steps 1–3 are slices of the one form; step 4
// stages documents locally. Nothing is written until Finish Setup on step 4,
// which creates the crew and uploads photos + staged documents. Step 5 is
// the success card — not a stepper entry, it just sits past the last one so
// every circle reads as complete.
type CrewStep = 1 | 2 | 3 | 4 | 5;

const LAST_FORM_STEP = 3;
const DOCS_STEP = 4;
const SUCCESS_STEP = 5;

const STEP_LABELS = ['Personal', 'Preferences', 'Access', 'Documents'] as const;

interface CrewDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  crewId?: string; // If provided, drawer opens in edit mode
  onSuccess?: () => void; // Called after successful create/update
}

export function CrewDrawer({ isOpen, onClose, crewId, onSuccess }: CrewDrawerProps) {
  const isEditMode = !!crewId;

  // The wizard writes the crew only at Finish Setup on the documents step. The
  // created id is kept just to hydrate the success card (avatar via
  // useCrewGetById) and to treat dismissing that card as "done". Edits always
  // target the prop id.
  const [createdCrewId, setCreatedCrewId] = useState<string | null>(null);
  const activeCrewId = crewId ?? createdCrewId;

  // Fetch crew data once we have an id (edit mode, or post-create in the wizard)
  const { data: crewData } = useCrewGetById(activeCrewId);

  // Mutations
  const createMutation = useCrewCreate();
  const updateMutation = useCrewUpdate(crewId || '');
  const checkEmailMutation = useCrewCheckEmailExists();
  const setAdminMutation = useCrewSetAdmin();
  const setPhotoMutation = useCrewSetPhoto();
  const { data: currentUser } = useCrewGetMe();

  const photoPickerRef = useRef<ProfilePhotoPickerHandle>(null);
  const prefPhotoRefs = useRef<Record<string, StagedPhotoGridHandle | null>>({});
  const [hasPendingPhoto, setHasPendingPhoto] = useState(false);
  const [isFlushingPhotos, setIsFlushingPhotos] = useState(false);
  const isSavingRef = useRef(false);
  const [pendingPrefPhotoCounts, setPendingPrefPhotoCounts] = useState<Record<string, number>>({});
  const pendingPrefPhotoTotal = Object.values(pendingPrefPhotoCounts).reduce((a, b) => a + b, 0);
  const [sendInvitation, setSendInvitation] = useState(true);

  // Create mode is a wizard; edit mode stays a single scrolling form with every
  // section visible at once.
  const [currentStep, setCurrentStep] = useState<CrewStep>(1);
  const [isAddDocumentOpen, setIsAddDocumentOpen] = useState(false);
  // Documents staged locally on step 4 (the crew doesn't exist yet); Finish
  // Setup uploads them right after creating the record.
  const [stagedDocuments, setStagedDocuments] = useState<StagedDocument[]>([]);

  const getUploadPost = useS3GetUploadPost();
  const createDocumentMutation = useDocumentCreate();

  const photosByPreference = useMemo(() => {
    const photos = (crewData as { preferencePhotos?: { preference: string; s3Key: string }[] } | undefined)
      ?.preferencePhotos ?? [];
    return photos.reduce<Record<string, string[]>>((acc, p) => {
      acc[p.preference] = [...(acc[p.preference] ?? []), p.s3Key];
      return acc;
    }, {});
  }, [crewData]);

  // Photos under a preference textarea (compact camera-button trigger per the
  // design); keys match the API's CrewPreferenceKeySchema.
  const renderPreferencePhotos = (preference: string) => (
    <div className="pb-3">
      <CrewPreferencePhotos
        key={`${preference}-${crewId ?? 'new'}`}
        ref={(r) => { prefPhotoRefs.current[preference] = r; }}
        preference={preference}
        s3Keys={photosByPreference[preference] ?? []}
        trigger="icon"
        onPendingChange={(count) =>
          setPendingPrefPhotoCounts((cur) => (cur[preference] === count ? cur : { ...cur, [preference]: count }))
        }
      />
    </div>
  );
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);
  // The form runs in onChange mode, so the check below fires on every
  // keystroke and the replies can land out of order. Without this, a stale
  // "not taken" from an earlier, shorter value clears the error the final
  // value set, and the wizard lets a duplicate through to the last step.
  const emailCheckSeq = useRef(0);
  const [isNationalityPickerOpen, setIsNationalityPickerOpen] = useState(false);
  const [isAirportPickerOpen, setIsAirportPickerOpen] = useState(false);
  const [isPhoneCodePickerOpen, setIsPhoneCodePickerOpen] = useState(false);
  const [isEmergencyCodePickerOpen, setIsEmergencyCodePickerOpen] = useState(false);

  const defaultValues: CrewFormData = {
    firstName: '',
    lastName: '',
    email: '',
    phoneCountryCode: '+1',
    phoneNumber: '',
    baseAirportIcao: '',
    nationality: '',
    dateOfBirth: undefined as unknown as Date,
    emergencyContactName: '',
    emergencyContactRelation: '',
    emergencyCountryCode: '+1',
    emergencyNumber: '',
    medicalInfo: '',
    foodPreferences: '',
    beveragePreferences: '',
    crewRole: null,
    pilotLicenseNumber: '',
    isAdmin: false,
    includeInSchedule: true,
  };

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    trigger,
    formState: { errors, isValid, isDirty, isSubmitting },
  } = useForm<CrewFormData>({
    resolver: zodResolver(crewFormSchema),
    defaultValues,
    mode: 'onChange',
  });

  // Plain state so the saving UI survives the mid-submit reset(): the update
  // mutation's invalidation refetches crewData while photos are still
  // uploading, and RHF's reset() unconditionally clears isSubmitting.
  const isSaving = isSubmitting || createMutation.isPending || updateMutation.isPending || isFlushingPhotos;

  // Populate form when drawer opens
  useEffect(() => {
    if (isSavingRef.current) return;
    if (isOpen) {
      if (isEditMode && crewData) {
        const parsedPhone = parsePhoneNumber(crewData.phone || '');
        const parsedEmergencyPhone = parsePhoneNumber(crewData.emergencyContactPhone || '');
        reset({
          firstName: crewData.firstName || '',
          lastName: crewData.lastName || '',
          email: crewData.email,
          phoneCountryCode: crewData.phoneCountry || parsedPhone.countryCode,
          phoneNumber: parsedPhone.number,
          baseAirportIcao: crewData.baseAirportIcao || '',
          nationality: crewData.nationality || '',
          // dayjs parses "YYYY-MM-DD" as local midnight; new Date() would parse it
          // as UTC midnight, shifting the picker a day back in UTC− timezones.
          dateOfBirth: crewData.dateOfBirth ? dayjs(crewData.dateOfBirth).toDate() : undefined as unknown as Date,
          emergencyContactName: crewData.emergencyContactName || '',
          emergencyContactRelation: crewData.emergencyContactRelation || '',
          emergencyCountryCode: crewData.emergencyContactPhoneCountry || parsedEmergencyPhone.countryCode,
          emergencyNumber: parsedEmergencyPhone.number,
          medicalInfo: crewData.medicalInfo || '',
          foodPreferences: crewData.foodPreferences || '',
          beveragePreferences: crewData.beveragePreferences || '',
          crewRole: crewData.crewRole,
          pilotLicenseNumber: crewData.pilotLicenseNumber || '',
          isAdmin: crewData.isAdmin ?? false,
          includeInSchedule: crewData.includeInSchedule ?? true,
        });
        // Trigger validation so isValid reflects the server data
        trigger();
      } else if (!isEditMode && !createdCrewId) {
        // Skipped once the crew is created — the wizard is on its success card
        // and a re-fire here would throw the user back to step 1.
        reset(defaultValues);
        setCurrentStep(1);
      }
    }
  }, [isOpen, isEditMode, crewData, createdCrewId, reset, trigger]);

  const watchedBaseAirportIcao = watch('baseAirportIcao');
  const watchedNationality = watch('nationality');
  const watchedPhoneCountryCode = watch('phoneCountryCode');
  const watchedEmergencyCountryCode = watch('emergencyCountryCode');
  const watchedCrewRole = watch('crewRole');
  const watchedPilotLicenseNumber = watch('pilotLicenseNumber');
  const watchedFirstName = watch('firstName');
  const watchedLastName = watch('lastName');
  const watchedEmail = watch('email');
  const watchedPhoneNumber = watch('phoneNumber');
  const watchedIsAdmin = watch('isAdmin');

  // Get base airport info for display
  const baseAirport = useAirportByIcao(watchedBaseAirportIcao);

  // Mirrors the schema's pilot-licence refine directly rather than reading
  // errors.pilotLicenseNumber: in onChange mode RHF only refreshes the error of
  // the field that changed, so picking a pilot role doesn't reliably flag the
  // (untouched) licence field.
  const needsPilotLicense = isPilotRole(watchedCrewRole) && !watchedPilotLicenseNumber?.trim();

  // Gate for the wizard's Next button. A derived boolean rather than RHF's
  // trigger(), matching FlightDrawer's isStep1Valid. Steps 2–3 hold only
  // optional fields, so they never block.
  const isStep1Valid =
    !!watchedFirstName &&
    !!watchedLastName &&
    !!watchedEmail &&
    !!watchedPhoneNumber &&
    !errors.firstName &&
    !errors.lastName &&
    !errors.email &&
    !errors.phoneNumber &&
    // Optional field, but it now lives on this step — a malformed number has to
    // block Next rather than surface later as a silently-refused Create.
    !errors.emergencyNumber &&
    !isValidatingEmail &&
    !emailError &&
    !needsPilotLicense;

  // The Create button submits the whole form, so a bad value left behind on an
  // earlier step needs to be visible rather than silently blocking the submit.
  // The licence is deliberately errors-only: needsPilotLicense fires the instant
  // a pilot role is picked, and badging the step you're standing on is just
  // noise — the disabled Next already communicates it.
  const stepErrors: Partial<Record<CrewStep, boolean>> = {
    1: !!(
      errors.firstName ||
      errors.lastName ||
      errors.email ||
      errors.phoneNumber ||
      emailError ||
      errors.crewRole ||
      errors.pilotLicenseNumber ||
      errors.emergencyNumber
    ),
  };

  const handleNext = () =>
    setCurrentStep((step) => (Math.min(step + 1, DOCS_STEP) as CrewStep));
  const handleBack = () => setCurrentStep((step) => (Math.max(step - 1, 1) as CrewStep));

  // Visible in edit mode (single scrolling page) or when it's the active create step
  const sectionHidden = (step: CrewStep) => !isEditMode && currentStep !== step;

  // Async email validation
  const validateEmailNotExists = async (email: string): Promise<string | true> => {
    if (!email) return true; // Empty email is valid (handled by schema)

    // Basic email format check first
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return true; // Let Zod handle format errors

    const seq = ++emailCheckSeq.current;
    const isStale = () => seq !== emailCheckSeq.current;

    setIsValidatingEmail(true);
    try {
      const result = await checkEmailMutation.mutateAsync({ email });
      if (isStale()) return true;
      if (result.exists) {
        setEmailError('This email is already registered');
        return 'This email is already registered';
      }
      setEmailError(null);
      return true;
    } catch {
      // If check fails, allow submission (don't block on network errors).
      // The server rejects duplicates on its own, so the worst case is the
      // message arriving on submit rather than on this step.
      if (!isStale()) setEmailError(null);
      return true;
    } finally {
      if (!isStale()) setIsValidatingEmail(false);
    }
  };

  const resetForm = () => {
    reset({
      firstName: '',
      lastName: '',
      email: '',
      phoneCountryCode: '+1',
      phoneNumber: '',
      baseAirportIcao: '',
      nationality: '',
      dateOfBirth: undefined as unknown as Date,
      emergencyContactName: '',
      emergencyContactRelation: '',
      emergencyCountryCode: '+1',
      emergencyNumber: '',
      medicalInfo: '',
      foodPreferences: '',
      beveragePreferences: '',
      crewRole: null,
      pilotLicenseNumber: '',
      isAdmin: false,
      includeInSchedule: true,
    });
    setSendInvitation(true);
    setEmailError(null);
    setCurrentStep(1);
    setCreatedCrewId(null);
    setIsAddDocumentOpen(false);
    setStagedDocuments([]);
  };

  // Photo uploads/removals run after the save so create mode has an id to
  // attach them to. Failures only toast — the crew record is already saved
  // (each preference grid also toasts per file).
  const flushPhotos = async (id: string) => {
    setIsFlushingPhotos(true);
    try {
      try {
        await photoPickerRef.current?.flushPending(id);
      } catch (error) {
        console.error('Failed to save crew photo:', error);
        toast.error('Failed to save photo');
      }
      for (const ref of Object.values(prefPhotoRefs.current)) {
        try {
          await ref?.flushPending(id);
        } catch (error) {
          console.error('Failed to save preference photos:', error);
        }
      }
    } finally {
      setIsFlushingPhotos(false);
    }
  };

  // Staged documents upload right after the crew is created (mirrors
  // FlightDrawer's flush): the caller toasts on failure but keeps going — the
  // crew record already exists.
  const flushStagedDocuments = async (id: string) => {
    for (const staged of stagedDocuments) {
      const s3Key = generateS3Key(id, staged.subType ?? DocumentSubType.Other, staged.file.name);
      await uploadFileWithPresignedPost(
        staged.file,
        s3Key,
        getUploadPost.mutateAsync as unknown as (input: {
          s3Key: string;
          contentType: string;
        }) => Promise<{ url: string; fields: Record<string, string> }>
      );
      await createDocumentMutation.mutateAsync({
        crewId: id,
        category: 'Crew',
        title: staged.title,
        fileName: staged.file.name,
        subType: staged.subType,
        s3Key,
        documentNumber: staged.documentNumber,
        issuer: staged.issuer,
        issueDate: staged.issueDate,
        expiryDate: staged.expiryDate ?? undefined,
        isPublic: staged.isPublic,
      });
      track(EVENTS.DOCUMENT_UPLOADED, { category: 'Crew', subType: staged.subType });
    }
  };

  const onSubmit = async (data: CrewFormData) => {
    isSavingRef.current = true;
    try {
      if (isEditMode && crewId) {
        // Update admin status if changed (and not attempting self-demotion)
        const nextIsAdmin = data.isAdmin ?? false;
        const prevIsAdmin = crewData?.isAdmin ?? false;
        const isSelf = crewId === currentUser?.id;
        if (nextIsAdmin !== prevIsAdmin && !(isSelf && !nextIsAdmin)) {
          await setAdminMutation.mutateAsync({ id: crewId, isAdmin: nextIsAdmin });
        }
        // Update existing crew member.
        // Cleared fields go out as '' rather than undefined: crewUpdateInputSchema
        // is all-optional and Prisma ignores undefined keys, so `|| undefined`
        // would silently leave the old value in the database.
        await updateMutation.mutateAsync({
          id: crewId,
          email: data.email || undefined,
          name: `${data.firstName} ${data.lastName}`.trim(),
          firstName: data.firstName,
          lastName: data.lastName,
          phoneCountry: data.phoneCountryCode,
          phone: data.phoneNumber,
          baseAirportIcao: data.baseAirportIcao || '',
          nationality: data.nationality || '',
          dateOfBirth: data.dateOfBirth ? dayjs(data.dateOfBirth).format('YYYY-MM-DD') : '',
          emergencyContactName: data.emergencyContactName || '',
          emergencyContactRelation: data.emergencyContactRelation || '',
          emergencyContactPhoneCountry: data.emergencyCountryCode || '',
          emergencyContactPhone: data.emergencyNumber || '',
          medicalInfo: data.medicalInfo,
          foodPreferences: data.foodPreferences,
          beveragePreferences: data.beveragePreferences,
          crewRole: data.crewRole ?? undefined,
          pilotLicenseNumber: isPilotRole(data.crewRole) ? data.pilotLicenseNumber ?? '' : '',
          includeInSchedule: data.includeInSchedule
        });
        await flushPhotos(crewId);
      } else {
        // Create new crew member
        const created = await createMutation.mutateAsync({
          name: `${data.firstName} ${data.lastName}`.trim(),
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phoneCountry: data.phoneCountryCode,
          phone: data.phoneNumber,
          baseAirportIcao: data.baseAirportIcao || undefined,
          nationality: data.nationality || undefined,
          dateOfBirth: data.dateOfBirth ? dayjs(data.dateOfBirth).format('YYYY-MM-DD') : undefined,
          emergencyContactName: data.emergencyContactName || undefined,
          emergencyContactRelation: data.emergencyContactRelation || undefined,
          emergencyContactPhoneCountry: data.emergencyCountryCode || undefined,
          emergencyContactPhone: data.emergencyNumber || undefined,
          medicalInfo: data.medicalInfo,
          foodPreferences: data.foodPreferences,
          beveragePreferences: data.beveragePreferences,
          shouldSendInvitation: sendInvitation,
          crewRole: data.crewRole ?? undefined,
          pilotLicenseNumber: isPilotRole(data.crewRole) ? data.pilotLicenseNumber || undefined : undefined,
          includeInSchedule: data.includeInSchedule,
          isAdmin: data.isAdmin
        });
        const createdId = (created as { id?: string } | undefined)?.id;
        if (createdId) {
          await flushPhotos(createdId);
          // Doc uploads are non-fatal past this point — the crew exists, so a
          // failed upload warns rather than aborting the wizard.
          try {
            await flushStagedDocuments(createdId);
          } catch (error) {
            console.error('Failed to upload staged documents:', error);
            toast.error('Crew member added, but some documents could not be uploaded');
          }
          // Stay open on the success card; Done (or dismissing) finishes up.
          setCreatedCrewId(createdId);
          setCurrentStep(SUCCESS_STEP);
          return;
        }
      }

      resetForm();
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error('Failed to save crew member:', error);
      const fallback = isEditMode ? 'Failed to update crew member' : 'Failed to add crew member';
      toast.error(error instanceof Error && error.message ? error.message : fallback);
    } finally {
      isSavingRef.current = false;
    }
  };

  const handleAddDocument = () => {
    hapticLight();
    setIsAddDocumentOpen(true);
  };

  // Finish the create wizard from the success card. The crew member already
  // exists by then, so dismissing is a "done", not a "discard".
  const handleFinish = () => {
    resetForm();
    onClose();
    onSuccess?.();
  };

  const handleCancel = () => {
    if (isSaving) return;
    // createdCrewId only exists once Finish Setup has run, i.e. on the success
    // card — dismissing there is a "done", not a "discard".
    if (!isEditMode && createdCrewId) {
      handleFinish();
      return;
    }
    if (!isEditMode) {
      resetForm();
    } else {
      reset();
    }
    setEmailError(null);
    onClose();
  };

  return (
    <>
      <Drawer open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
        <DrawerContent className="bg-surface-L0 flex flex-col max-h-[85dvh]">
          {/* Title and stepper share the sticky header: DrawerContent drops every
              non-footer child into one scroll area, so a stepper outside this
              block would scroll up out of reach behind the pinned title. */}
          <DrawerHeader className="flex flex-col gap-0 p-0 shrink-0">
            <div className="flex items-center justify-between p-4">
              <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
                {isEditMode ? 'Edit Crew Member' : 'Add Crew Member'}
              </DrawerTitle>
              <Button variant="secondary" size="icon" roundness="round" className="size-8" onClick={handleCancel}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            {!isEditMode && (
              <Stepper
                className="pb-3"
                steps={STEP_LABELS.map((label, index) => ({
                  label,
                  hasError: stepErrors[(index + 1) as CrewStep],
                }))}
                currentStep={currentStep}
                // Withheld only on the terminal success card. Navigation is
                // otherwise free: nothing is written until Finish Setup, which
                // carries the full validity gate.
                onStepClick={
                  currentStep <= DOCS_STEP
                    ? (step) => setCurrentStep(step as CrewStep)
                    : undefined
                }
              />
            )}
          </DrawerHeader>

          {/* Scrollable Content */}
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
            {/* Every section stays mounted and is hidden with CSS: unmounting would
                drop the staged preference photos and the profile photo ref, and the
                final submit needs every form step's values. */}
            <form
              id="crew-form"
              onSubmit={handleSubmit(onSubmit)}
              className={cn(
                'flex flex-col gap-4',
                isSaving && 'pointer-events-none',
                !isEditMode && currentStep > LAST_FORM_STEP && 'hidden'
              )}
            >
              {/* Step 1 — Personal */}
              <div className={cn('flex flex-col gap-4', sectionHidden(1) && 'hidden')}>
                {/* Design drops the photo picker from the add wizard; the photo
                    can be set later via edit mode. */}
                {isEditMode && (
                  <ProfilePhotoPicker
                    ref={photoPickerRef}
                    key={crewId ?? 'new'}
                    currentS3Key={crewData?.photoS3Key}
                    initials={getInitials(watchedFirstName, watchedLastName)}
                    avatarColor={crewData?.avatarColor}
                    generateS3Key={generateCrewPhotoS3Key}
                    setPhoto={setPhotoMutation.mutateAsync as unknown as (input: { id: string; s3Key: string | null }) => Promise<unknown>}
                    onPendingChange={setHasPendingPhoto}
                  />
                )}

                <PrntrCard title="Personal Details">
                  <TextRow
                    control={control}
                    name="firstName"
                    label="First Name *"
                    nullable={false}
                    error={errors.firstName?.message}
                  />
                  <TextRow
                    control={control}
                    name="lastName"
                    label="Last Name *"
                    nullable={false}
                    error={errors.lastName?.message}
                  />
                  <TextRow
                    control={control}
                    name="email"
                    label="Email Address *"
                    type="email"
                    inputMode="email"
                    autoCapitalize="none"
                    nullable={false}
                    rules={{ validate: isEditMode ? undefined : validateEmailNotExists }}
                    decoration={
                      isValidatingEmail ? (
                        <span className="shrink-0 text-xs text-content-primary-subtle">Checking…</span>
                      ) : undefined
                    }
                    error={errors.email?.message || emailError || undefined}
                  />
                  <PhoneRowField
                    control={control}
                    codeName="phoneCountryCode"
                    numberName="phoneNumber"
                    label="Telephone Number *"
                    onCodeClick={() => setIsPhoneCodePickerOpen(true)}
                    error={errors.phoneNumber?.message}
                  />
                  <PickerRowField
                    control={control}
                    name="nationality"
                    label="Nationality"
                    display={(value) => (value as string) || null}
                    onOpen={() => setIsNationalityPickerOpen(true)}
                    hideChevron
                    error={errors.nationality?.message}
                  />
                  <PickerRowField
                    control={control}
                    name="baseAirportIcao"
                    label="Base"
                    display={(value) =>
                      baseAirport
                        ? `${baseAirport.icaoCode} · ${baseAirport.municipality || baseAirport.name}`
                        : ((value as string) || null)
                    }
                    onOpen={() => setIsAirportPickerOpen(true)}
                    hideChevron
                  />
                  <DateObjectRow
                    control={control}
                    name="dateOfBirth"
                    label="Date of Birth"
                    title="Date of Birth"
                    startMonth={new Date(1920, 0)}
                    endMonth={new Date()}
                    error={errors.dateOfBirth?.message}
                  />
                </PrntrCard>

                {/* Role — its own card so the step doesn't read as one long list */}
                <PrntrCard title="Crew Role">
                  <SelectRowField
                    control={control}
                    name="crewRole"
                    label="Role"
                    options={crewRoleOptions}
                    placeholder="Select role"
                    error={errors.crewRole?.message}
                  />
                  {/* Pilot License Number — pilots only, required */}
                  {isPilotRole(watchedCrewRole) && (
                    <TextRow
                      control={control}
                      name="pilotLicenseNumber"
                      label="Pilot License Number *"
                      nullable={false}
                      error={errors.pilotLicenseNumber?.message}
                    />
                  )}
                </PrntrCard>

                <PrntrCard title="Emergency Contact">
                  <TextRow
                    control={control}
                    name="emergencyContactName"
                    label="Full Name"
                    nullable={false}
                    error={errors.emergencyContactName?.message}
                  />
                  <TextRow
                    control={control}
                    name="emergencyContactRelation"
                    label="Relation"
                    nullable={false}
                    error={errors.emergencyContactRelation?.message}
                  />
                  <PhoneRowField
                    control={control}
                    codeName="emergencyCountryCode"
                    numberName="emergencyNumber"
                    label="Telephone Number"
                    onCodeClick={() => setIsEmergencyCodePickerOpen(true)}
                    error={errors.emergencyNumber?.message}
                  />
                </PrntrCard>
              </div>

              {/* Step 2 — Preferences: one card of textareas, each with its own
                  compact photo trigger */}
              <div className={cn('flex flex-col', sectionHidden(2) && 'hidden')}>
                <PrntrCard>
                  <TextareaField control={control} name="medicalInfo" label="Medical Information" />
                  {renderPreferencePhotos('medicalInfo')}
                  <TextareaField control={control} name="foodPreferences" label="Food Preferences" />
                  {renderPreferencePhotos('foodPreferences')}
                  <TextareaField control={control} name="beveragePreferences" label="Beverage Preferences" />
                  {renderPreferencePhotos('beveragePreferences')}
                </PrntrCard>
              </div>

              {/* Step 3 — Access: the scheduling/permission toggles */}
              <div className={cn('flex flex-col gap-4', sectionHidden(3) && 'hidden')}>
                <PrntrCard>
                  <Controller
                    name="includeInSchedule"
                    control={control}
                    render={({ field }) => (
                      <PrntrSwitchRow
                        label={toggleLabel(
                          'Include in schedule',
                          'Show in roster, trips and flight assignments'
                        )}
                        checked={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  {/* Admin toggle — only visible to admins; self-demotion locked */}
                  {currentUser?.isAdmin &&
                    (() => {
                      const isSelf = crewId === currentUser?.id;
                      const selfLocked = isSelf && (crewData?.isAdmin ?? false);
                      return (
                        <Controller
                          name="isAdmin"
                          control={control}
                          render={({ field }) => (
                            <PrntrSwitchRow
                              label={toggleLabel(
                                'Grant admin access',
                                selfLocked
                                  ? "You can't revoke your own admin access"
                                  : 'Grant admin access to manage the company'
                              )}
                              checked={field.value}
                              readOnly={selfLocked}
                              onChange={(checked) => {
                                if (isSelf && !checked) return;
                                field.onChange(checked);
                              }}
                            />
                          )}
                        />
                      );
                    })()}
                  <PrntrSwitchRow
                    label={toggleLabel('Send an invitation to log in', 'Email an invitation to join the platform')}
                    checked={sendInvitation}
                    onChange={setSendInvitation}
                  />
                </PrntrCard>
              </div>
            </form>

            {/* Step 4 (create only): documents staged locally — the crew doesn't
                exist yet, so tapping a row removes it rather than opening a viewer */}
            {!isEditMode && currentStep === DOCS_STEP && (
              <div className="flex flex-col gap-3 pt-2">
                {stagedDocuments.length > 0 ? (
                  <>
                    <DocumentsGroupCard
                      name={
                        `${watchedFirstName} ${watchedLastName}`.trim() || 'New crew member'
                      }
                      documents={stagedDocuments.map((doc, index) => ({
                        id: String(index),
                        title: doc.title,
                        subType: doc.subType,
                        expiryDate: doc.expiryDate,
                      }))}
                      onDocumentClick={(documentId) =>
                        setStagedDocuments((prev) => prev.filter((_, i) => i !== Number(documentId)))
                      }
                    />
                    <Button
                      variant="secondary"
                      size="mini"
                      className="self-start text-content-brand-bold"
                      onClick={handleAddDocument}
                    >
                      <Plus className="size-4" />
                      Add Document
                    </Button>
                  </>
                ) : (
                  <EmptyStateCard label="Add a document" onAdd={handleAddDocument} />
                )}
              </div>
            )}

            {/* Step 5 (create only): terminal success card. Not a stepper entry —
                currentStep sits past the last circle so they all read complete. */}
            {!isEditMode && currentStep === SUCCESS_STEP && (
              <div className="flex flex-col items-center gap-4 px-8 pb-6 pt-8">
                <div className="relative">
                  <ProfileAvatar
                    photoS3Key={crewData?.photoS3Key}
                    avatarColor={crewData?.avatarColor}
                    initials={getInitials(watchedFirstName, watchedLastName)}
                    className="size-8"
                    fallbackClassName="text-sm text-content-primary-inverse"
                  />
                  {/* 6px halo in the avatar's own colour at 40% (design: border-6) */}
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 rounded-full"
                    style={
                      crewData?.avatarColor
                        ? { boxShadow: `0 0 0 6px ${crewData.avatarColor}66` }
                        : undefined
                    }
                  />
                  {watchedIsAdmin && (
                    <span className="absolute -right-2 -top-1 flex items-center justify-center rounded-full bg-content-brand-bold p-[3px]">
                      <Crown className="size-3 text-content-primary-inverse" />
                    </span>
                  )}
                </div>
                <p className="text-center text-xl font-bold tracking-[-1px] text-content-primary-bold">
                  Crew Member Added
                </p>
                <p className="text-center text-sm text-content-primary-subtle">
                  {`${watchedFirstName} ${watchedLastName}`.trim() || 'They'} has been added to
                  your crew.
                </p>
                <Button size="mini" onClick={handleFinish} className="px-6">
                  Done
                </Button>
              </div>
            )}
          </div>

          {/* Footer — omitted on the success card, which carries its own Done */}
          {currentStep !== SUCCESS_STEP && (
            <DrawerFooter>
              {isEditMode ? (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="mini"
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    size="mini"
                    form="crew-form"
                    disabled={(!isDirty && !hasPendingPhoto && pendingPrefPhotoTotal === 0) || !isValid || isSaving || isValidatingEmail || !!emailError}
                    className="flex-1"
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                  </Button>
                </div>
              ) : currentStep === DOCS_STEP ? (
                <Button
                  type="button"
                  size="mini"
                  onClick={handleSubmit(onSubmit)}
                  disabled={!isValid || isSaving || isValidatingEmail || !!emailError}
                  className="w-full"
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Finish Setup'}
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="mini"
                    onClick={currentStep === 1 ? handleCancel : handleBack}
                    disabled={isSaving}
                    className="flex-1"
                  >
                    {currentStep === 1 ? 'Cancel' : 'Back'}
                  </Button>
                  <Button
                    type="button"
                    size="mini"
                    onClick={handleNext}
                    disabled={currentStep === 1 && !isStep1Valid}
                    className="flex-1"
                  >
                    Next
                  </Button>
                </div>
              )}
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>

      {/* Nationality Picker Drawer */}
      <NationalityPickerDrawer
        isOpen={isNationalityPickerOpen}
        onClose={() => setIsNationalityPickerOpen(false)}
        selectedNationality={watchedNationality}
        onSelect={(nationality) => setValue('nationality', nationality, { shouldDirty: true, shouldValidate: true })}
      />

      {/* Airport Picker Drawer */}
      <AirportPickerDrawer
        isOpen={isAirportPickerOpen}
        onClose={() => setIsAirportPickerOpen(false)}
        onSelect={(icaoCode) => setValue('baseAirportIcao', icaoCode, { shouldDirty: true, shouldValidate: true })}
        title="Select Base"
        searchParam={watchedBaseAirportIcao}
      />

      {/* Phone Country Code Picker */}
      <CountryCodePickerDrawer
        isOpen={isPhoneCodePickerOpen}
        onClose={() => setIsPhoneCodePickerOpen(false)}
        selectedCode={watchedPhoneCountryCode}
        onSelect={(code) => setValue('phoneCountryCode', code, { shouldDirty: true })}
      />

      {/* Emergency Contact Country Code Picker */}
      <CountryCodePickerDrawer
        isOpen={isEmergencyCodePickerOpen}
        onClose={() => setIsEmergencyCodePickerOpen(false)}
        selectedCode={watchedEmergencyCountryCode}
        onSelect={(code) => setValue('emergencyCountryCode', code, { shouldDirty: true })}
      />

      {/* Documents step (create wizard): the crew doesn't exist yet, so the
          drawer stages the form values + File locally instead of uploading */}
      {!isEditMode && (
        <DocumentFormDrawer
          isOpen={isAddDocumentOpen}
          owner={{ type: 'crew' }}
          onClose={() => setIsAddDocumentOpen(false)}
          onStage={(doc) => setStagedDocuments((prev) => [...prev, doc])}
        />
      )}
    </>
  );
}
